import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { useAppSelector, useAppStore } from '@/lib/hooks';

import FormModal from '@/common/components/modals/general/FormModal';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';
import { AddIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import { CourseData } from 'course-types';

interface FoundUser {
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
}

const normalizeEmailInput = (value: string): string => value.trim().toLowerCase();

function AddCourseAdminModal() {
  const store = useAppStore();
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const [userEmail, setUserEmail] = useState<string>('');
  const [foundUser, setFoundUser] = useState<FoundUser | undefined>();
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const resetForm = () => {
    setUserEmail('');
    setFoundUser(undefined);
    setIsSearching(false);
  };

  const findCollaborator = async () => {
    if (!userEmail.trim()) return;

    setIsSearching(true);
    try {
      const response = await (
        await Request.post('admin/find/enterprise', {
          email: normalizeEmailInput(userEmail),
          courseId: activeCourseData.id,
        })
      )?.json();

      if (!response || response.error || !response.user) {
        throw new Error('Collaborator not found. Please check email and try again.');
      }

      setFoundUser(response.user);
      toast.success('Successfully found collaborator account.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to find collaborator');
      setFoundUser(undefined);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!foundUser) {
      throw new Error('No collaborator found to add');
    }

    const response = await (
      await Request.post('admin/add/newCourseAdmin', {
        courseId: activeCourseData.id,
        email: normalizeEmailInput(foundUser.email),
      })
    )?.json();

    if (!response || response.error || !response.status) {
      throw new Error('Failed to add collaborator to course');
    }

    toast.success('Successfully added course collaborator');
    resetForm();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value);
    if (foundUser) {
      setFoundUser(undefined);
    }
  };

  return (
    <FormModal
      modalName='AddCollaborator'
      title='Add Course Collaborator'
      subtitle='Add an instructor or admin as a course collaborator'
      submitButton={{
        text: 'Add Collaborator',
        icon: <AddIcon className='w-[24px] h-[24px] mr-2' />,
        disabled: !foundUser,
      }}
      onSubmit={handleSubmit}
      className='max-w-[500px] w-full'
      loadingText='Adding collaborator...'
    >
      <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300'>
        <p className='text-base'>Only instructors and admins may be added as collaborators.</p>
        <p className='text-sm text-text_gray_900'>
          Collaborators will be given full access to edit the current selected course.
        </p>
      </div>

      {!foundUser ? (
        <div className='flex flex-col gap-4'>
          <StandardInputText
            id='userEmail'
            label='Collaborator Email'
            isRequired={true}
            value={userEmail}
            onChange={handleEmailChange}
            disabled={isSearching}
          />

          <Button
            onClick={findCollaborator}
            disabled={!userEmail.trim() || isSearching}
            className='w-fit'
          >
            {isSearching ? 'Searching...' : 'Find Collaborator'}
          </Button>
        </div>
      ) : (
        <div className='flex flex-col gap-2 bg-surface_gray_50 rounded-lg p-4 border border-border_gray_300'>
          <p className='text-base font-semibold'>Collaborator Found</p>
          <p className='text-lg'>
            {foundUser.first_name} {foundUser.last_name}
          </p>
          <p className='text-sm text-text_gray_900'>{foundUser.email}</p>
          {foundUser.role && (
            <p className='text-sm text-text_gray_900'>
              Role: {foundUser.role === 'enterprise_admin' ? 'Admin' : 'Instructor'}
            </p>
          )}

          <Button onClick={() => setFoundUser(undefined)} outline className='w-fit mt-2'>
            Search Different Collaborator
          </Button>
        </div>
      )}
    </FormModal>
  );
}

export default AddCourseAdminModal;
